/**
 * Google Tag Manager Event Tracking Utility
 * 
 * This utility helps you push events to GTM's dataLayer for tracking user interactions.
 * Use these functions in your React components to track events without modifying GTM directly.
 */

/**
 * Push an event to GTM dataLayer
 * @param {string} eventName - The name of the event (e.g., 'button_click', 'form_submit')
 * @param {object} eventData - Additional data to send with the event
 */
export const trackEvent = (eventName, eventData = {}) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...eventData,
    });
  } else {
    console.warn('GTM dataLayer not available');
  }
};

/**
 * Track button clicks
 * @param {string} buttonName - Name/ID of the button
 * @param {string} buttonLocation - Where the button is located (e.g., 'header', 'sidebar', 'landing_page')
 * @param {object} additionalData - Any additional data to track
 */
export const trackButtonClick = (buttonName, buttonLocation = '', additionalData = {}) => {
  trackEvent('button_click', {
    button_name: buttonName,
    button_location: buttonLocation,
    ...additionalData,
  });
};
export const trackButtonBookDemoClick = (buttonName, buttonLocation = '', additionalData = {}) => {
  trackEvent('book_demo_click', {
    button_name: buttonName,
    button_location: buttonLocation,
    ...additionalData,
  });
};
/**
 * Track link clicks/downloads
 * @param {string} linkUrl - The URL being clicked
 * @param {string} linkText - The text of the link
 * @param {string} linkType - Type of link ('download', 'external', 'internal')
 * @param {string} fileType - File type if it's a download (e.g., 'pdf', 'zip', 'exe')
 */
export const trackLinkClick = (linkUrl, linkText = '', linkType = 'internal', fileType = '') => {
  const isDownload = linkType === 'download' || 
    /\.(pdf|zip|exe|dmg|pkg|doc|docx|xls|xlsx|ppt|pptx|csv)$/i.test(linkUrl);
  
  trackEvent('link_click', {
    link_url: linkUrl,
    link_text: linkText,
    link_type: isDownload ? 'download' : linkType,
    file_type: fileType || (isDownload ? linkUrl.split('.').pop() : ''),
  });
};

/**
 * Track video interactions
 * @param {string} action - Action type ('play', 'pause', 'complete', 'progress')
 * @param {string} videoTitle - Title/name of the video
 * @param {string} videoUrl - URL of the video
 * @param {number} currentTime - Current playback time in seconds
 * @param {number} duration - Total video duration in seconds
 */
export const trackVideoEvent = (action, videoTitle = '', videoUrl = '', currentTime = 0, duration = 0) => {
  trackEvent('video_interaction', {
    video_action: action,
    video_title: videoTitle,
    video_url: videoUrl,
    video_current_time: currentTime,
    video_duration: duration,
    video_progress_percent: duration > 0 ? Math.round((currentTime / duration) * 100) : 0,
  });
};

/**
 * Track form submissions
 * @param {string} formName - Name/ID of the form
 * @param {string} formLocation - Where the form is located
 * @param {boolean} success - Whether the submission was successful
 * @param {object} additionalData - Additional form data
 */
export const trackFormSubmit = (formName, formLocation = '', success = true, additionalData = {}) => {
  trackEvent('form_submit', {
    form_name: formName,
    form_location: formLocation,
    form_success: success,
    ...additionalData,
  });
};

/**
 * Track page views (useful for SPA navigation)
 * @param {string} pagePath - The path of the page
 * @param {string} pageTitle - Title of the page
 */
export const trackPageView = (pagePath, pageTitle = '') => {
  trackEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle,
  });
};

/**
 * Track search events
 * @param {string} searchTerm - The search query
 * @param {string} searchCategory - Category of search (e.g., 'projects', 'pull_requests')
 * @param {number} resultsCount - Number of results returned
 */
export const trackSearch = (searchTerm, searchCategory = '', resultsCount = 0) => {
  trackEvent('search', {
    search_term: searchTerm,
    search_category: searchCategory,
    results_count: resultsCount,
  });
};

/**
 * Track file downloads
 * @param {string} fileName - Name of the file
 * @param {string} fileType - Type of file (e.g., 'pdf', 'csv', 'json')
 * @param {string} downloadLocation - Where the download was initiated from
 */
export const trackDownload = (fileName, fileType = '', downloadLocation = '') => {
  trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType,
    download_location: downloadLocation,
  });
};

/**
 * Track custom events with any data
 * @param {string} eventName - Custom event name
 * @param {object} eventData - Any custom data
 */
export const trackCustomEvent = (eventName, eventData = {}) => {
  trackEvent(eventName, eventData);
};
